import type { AccountDevice, ForkLeg, ForkProgress, ForkStage } from '../transport/api'
import { deviceDisplayName } from '../transport/deviceName'

/**
 * What went wrong when a conversation could not be forked onto another machine,
 * said in words a person can act on.
 *
 * The endpoint names every failure — avg-devenv, 2026-08-22: 錯誤全部具名，請直接把
 * 名字對應到人話，不要都顯示「失敗」. A name per condition, a sentence per name, and
 * deliberately no default that folds unknown ones into "something went wrong": an
 * unrecognised code is shown verbatim, because a code we can look up beats a
 * reassurance we made up.
 *
 * THE NAME HAS TO BE THE ONE THE MACHINE USES. On 2026-08-22 the owner pressed Fork on
 * a cursor-agent and got the unknown-code branch, because one condition had three
 * names: the edge throws `cross_fork_cli_unsupported`, the BFF forwards it as
 * `source_cross_fork_cli_unsupported`, and this table was keyed `fork_unsupported_cli`
 * — a name nothing emits. A sentence written for exactly that case had never once
 * reached a screen. Every name here is now grepped from the source that raises it, and
 * `forkSide` strips the side prefix so both shapes resolve.
 *
 * TWO RULES THIS TABLE EXISTS TO ENFORCE:
 *
 *  0. WHETHER ANYTHING WAS LEFT BEHIND is part of the message, and it is decided by
 *     WHERE in the import the failure happened. avg-devenv read the order out of
 *     agentRoutes.ts on 2026-08-22 and it lands either side of one line:
 *
 *       key → download → hash → decrypt → **writeFile** → find pane → start CLI
 *
 *     Everything before the write leaves the target machine untouched, and saying
 *     "nothing was imported" there is true. Everything after it does not: the
 *     conversation is on that machine, and for `started_and_exited` a tmux session was
 *     created too. Telling someone nothing was imported when a copy of their
 *     conversation is sitting on another machine is the same class of lie as the empty
 *     roster that cost two developers an afternoon — it just points the other way.
 *
 *  1. `decrypt_failed` MUST NOT read as a network problem. The transfer is encrypted
 *     end to end with AES-GCM, so a decrypt failure is not a dropped packet — it means
 *     the bytes that arrived are not the bytes that were sent. It is the one failure
 *     here that says something about SAFETY rather than about weather, and it does not
 *     get a one-tap retry: re-running a transfer whose integrity check failed is how
 *     you find out you were wrong twice.
 *
 *  2. `transfer_not_ready` / `transfer_download_failed` / `decrypt_failed` stay three
 *     different sentences. They are three different stages failing for three different
 *     reasons, and merging them is how the next person debugging this loses the only
 *     clue they had.
 *
 * `retry` is safe to expose wherever it is true: the endpoint is single-flight on the
 * same parameters for 90 seconds, so a second attempt joins the first rather than
 * starting a second fork (avg-latency, 2026-08-22: 不要自己再包一層防連點).
 */
export type ForkFailure = {
  /** One line, the thing that happened. */
  title: string
  /** What to do about it, or why nothing can be done. Empty when the title says all. */
  detail: string
  /** Offer to try again. False when trying again cannot help — or must not. */
  retry: boolean
}

/** `{target}` is filled by the caller's machine name — see `forkFailure`. */
const FAILURES: Record<string, ForkFailure> = {
  /* ── before the write: the target machine is untouched ───────────────────── */
  fork_key_missing: {
    title: 'The key never arrived',
    detail: 'The copy could not be unlocked, so nothing was imported. Nothing is running.',
    retry: true,
  },
  machine_not_here: {
    title: 'That machine isn’t connected right now',
    detail: 'The fork needs both machines online. Nothing was started; try again when it is back.',
    retry: true,
  },
  unknown_device: {
    title: 'That machine isn’t on this account',
    detail: 'Pick another one — this account can only reach machines it has paired.',
    retry: false,
  },
  /*
   * THE SAME-MACHINE FORK'S OWN REFUSALS. These reach forkFailure as bare codes
   * (no side prefix) via the thrown ApiError, and each is the machine answering
   * about the FOLDER — nothing was forked when any of them fires. They earned
   * their sentences the day every local fork surfaced as "Lost contact with the
   * cloud … cwd_unwritable" (owner, 2026-08-25).
   */
  cwd_unwritable: {
    title: 'That folder can’t be created on {target}',
    detail:
      'The machine could not make the folder — usually a path outside your home, or a permissions wall. Nothing was forked; pick a folder the machine can write.',
    retry: false,
  },
  not_a_directory: {
    title: 'That path is a file, not a folder',
    detail: 'Something already sits there that isn’t a directory. Nothing was forked; choose another path.',
    retry: false,
  },
  already_exists: {
    title: 'An agent with that name already exists',
    detail: 'Session names are addresses, one each. Nothing was forked; pick another name.',
    retry: false,
  },
  no_conversation: {
    title: 'There’s no conversation to carry the transfer',
    detail: 'The copy travels through a chat conversation. Start one — a project room or a DM — and try again.',
    retry: false,
  },
  /*
   * THE NAME THE MACHINE ACTUALLY USES. This entry was keyed `fork_unsupported_cli`,
   * which nothing in the system emits — the edge throws `cross_fork_cli_unsupported`
   * (agentRoutes.ts:1177, :1373) and the BFF forwards it as `source_cross_fork_cli_
   * unsupported`. Three names for one condition, so the lookup missed and a person
   * pressing Fork on a cursor-agent got the unknown-code branch instead of the
   * sentence written for exactly their case. The owner found it on the first tap.
   */
  cross_fork_cli_unsupported: {
    title: 'This agent’s CLI can’t be forked across machines yet',
    detail:
      'This agent’s conversation can’t be carried to another machine. Fork it on its own machine instead.',
    retry: false,
  },
  no_transfer_key: {
    title: 'The copy could not be sealed for sending',
    detail: 'Nothing left the source machine, and nothing was imported. Try again.',
    retry: true,
  },
  chat_unconfigured: {
    title: 'This cloud can’t carry a fork yet',
    detail: 'The copy travels through the chat lane, and it is not configured here. Neither machine was asked.',
    retry: false,
  },
  chat_unreachable: {
    title: 'The chat lane didn’t answer',
    detail: 'The copy travels through it, so nothing was sent and neither machine was asked. Try again.',
    retry: true,
  },
  no_transcript_yet: {
    title: 'That agent hasn’t said anything yet',
    detail: 'There is no conversation to copy. Let it work first, then fork it.',
    retry: true,
  },
  transfer_not_ready: {
    title: 'The copy wasn’t ready to send',
    detail: 'The source machine had not finished preparing it. Nothing was imported; try again.',
    retry: true,
  },
  transfer_download_failed: {
    title: 'The target machine couldn’t fetch the copy',
    detail: 'It was sent, but the download did not complete. Nothing was imported; try again.',
    retry: true,
  },
  decrypt_failed: {
    /*
     * NOT "a network problem", and not retried on a tap. AES-GCM authenticates what it
     * decrypts, so this is the check REFUSING content that does not match what was
     * sealed — the failure mode the encryption exists to have.
     */
    title: 'The copy didn’t arrive intact',
    detail:
      'It failed its integrity check, which means the bytes that arrived are not the bytes that were sent. Nothing was imported. Tell someone before trying again.',
    retry: false,
  },
  /*
   * THE CLOUD DOES NOT HAVE THIS ROUTE. Not a machine problem, not the user's.
   *
   * `POST /account/agents/fork-across` is dev-only today; production answers
   * `{"error":"unknown_route"}` — verified 2026-08-22. The picker is in the shipped
   * bundle either way (there is no capability flag, and inventing one would be a gate
   * to remember to remove), so without this entry the code fell into the
   * unknown-code branch and told the reader "the machine refused: unknown_route" and
   * that we could not say whether anything was left on their machine. Both are
   * wrong: no machine was ever asked, and nothing exists to have been left.
   *
   * Named here rather than gated, so it heals the moment the route ships.
   */
  unknown_route: {
    title: 'This cloud can’t fork to another machine yet',
    detail:
      'Nothing was started, and neither machine was asked. Forking on {target}’s own machine still works.',
    retry: false,
  },
  invalid_request: {
    title: 'The fork was asked for incorrectly',
    detail: 'This is a fault in the app, not in either machine. Nothing was started.',
    retry: false,
  },

  /* ── after the write: something IS on the target machine ─────────────────── */
  /*
   * None of these offers a retry. A second attempt does not clean up after the first
   * — it lands beside whatever is already there — and the honest instruction is to go
   * and look at the machine, which is the whole point of telling someone at all.
   */
  import_write_failed: {
    title: 'The copy could not be written on {target}',
    detail:
      'A partial file may be left on {target}. Nothing is running. Check it there before forking again.',
    retry: false,
  },
  cli_not_found: {
    title: 'The conversation arrived, but {target} has nothing to run it with',
    detail:
      'The copy IS on {target} — only the CLI is missing. Nothing is running; install it there, or remove the copy.',
    retry: false,
  },
  create_failed: {
    title: 'The conversation arrived on {target} but the agent would not start',
    detail: 'The copy IS on {target}. Nothing is running. Check the machine before forking again.',
    retry: false,
  },
  started_and_exited: {
    /*
     * The heaviest of them: BOTH the conversation file and a tmux session exist on the
     * target. "Nothing was imported" here would be wrong twice over.
     */
    title: 'The agent started on {target} and then exited',
    detail:
      'The conversation is on {target} and a session was created for it. Look at the machine before forking again — a second fork lands beside this one.',
    retry: false,
  },
}

/**
 * A named failure as a sentence, or the raw code when this build has not heard of it.
 *
 * An unknown code is NOT smoothed into a generic apology: a newer server saying
 * `quota_exceeded` should reach the reader as `quota_exceeded`, which they can search
 * for and we can grep, rather than as "something went wrong" — the sentence that has
 * never once helped anybody.
 */
/**
 * WHICH SIDE STOPPED IT, read off the code rather than guessed.
 *
 * `bff/app.py:3762` answers `f"{ref.side}_{ref.code}"` — `bff_`, `source_` or
 * `target_`. This build was treating the whole thing as one opaque name, so every
 * prefixed code missed the table AND the side was thrown away, and the side is the
 * half that decides what can be said about the target machine:
 *
 *   bff_*     neither machine was ever asked
 *   source_*  the source refused; nothing was sent, so the target is untouched
 *   target_*  it got as far as the target, and only the named codes know how far
 *
 * The same-machine fork (`forkAgent`) returns the edge's bare codes with no prefix, so
 * both shapes have to resolve.
 */
// Not exported: `forkFailure` is the only caller, and the side-prefix behaviour is
// pinned through it (forkAcross.test.ts, "side prefixes") rather than by reaching in.
function forkSide(code: string): { side: 'bff' | 'source' | 'target' | null; bare: string } {
  for (const side of ['bff', 'source', 'target'] as const) {
    if (code.startsWith(`${side}_`)) return { side, bare: code.slice(side.length + 1) }
  }
  return { side: null, bare: code }
}

export function forkFailure(
  code: string | undefined | null,
  message?: string | null,
  names?: { target?: string },
): ForkFailure {
  const to = names?.target?.trim() || 'the target machine'
  const raw = (code ?? '').trim()
  const { side, bare } = forkSide(raw)
  // The full code first: a prefixed name may one day want its own sentence.
  const known = raw ? (FAILURES[raw] ?? FAILURES[bare]) : undefined
  if (known) {
    return {
      ...known,
      title: known.title.replaceAll('{target}', to),
      detail: known.detail.replaceAll('{target}', to),
    }
  }
  if (raw) {
    /*
     * AN UNKNOWN BRANCH MUST NOT MAKE A CLAIM.
     *
     * This said "This build does not know whether anything was left on {target} —
     * check it before forking again", and the owner met it on the first thing he
     * tried: a cursor-agent, refused by the SOURCE machine before a single byte was
     * exported. Nothing had ever reached the target, and the sentence sent him to
     * inspect a machine where nothing had happened. avg-devenv put it exactly: unknown
     * has to mean "I do not know what happened", never "I know something may be left"
     * — the second is a specific claim, and it is the same shape as the `held` wording
     * that had to be pulled from the stuck marker earlier the same day.
     *
     * The side rescues most of it. `source_*` and `bff_*` failed before anything was
     * written, so "nothing reached {target}" is KNOWN, not guessed — the system had
     * that fact and this function was throwing it away. Only `target_*` and a bare
     * unknown code leave the question genuinely open, and there it says so without
     * dressing it as an instruction.
     */
    const untouched = side === 'source' || side === 'bff'
    const who =
      side === 'source'
        ? 'The source machine refused'
        : side === 'target'
          ? `${to} refused`
          : side === 'bff'
            ? 'The cloud refused'
            : 'The machine refused'
    const said = (message ?? '').trim()
    return {
      title: `${who}: ${bare}`,
      detail: [
        // The machine's own sentence first, and PUNCTUATED — these ran together on
        // screen as "…refused to export This build does not…".
        said ? (/[.!?]$/.test(said) ? said : `${said}.`) : '',
        untouched
          ? `Nothing reached ${to}.`
          : `This build does not recognise that code, so it cannot say how far the import got on ${to}.`,
      ]
        .filter(Boolean)
        .join(' '),
      retry: false,
    }
  }
  /*
   * NO CODE AT ALL means the request itself did not come back — the browser or the
   * hop in front of the BFF gave up. That is NOT the same as the fork failing, and
   * saying so would be the most expensive wrong sentence in this table.
   *
   * avg-latency measured why it matters: Caddy's `lb_try_duration 8s` exists so a BFF
   * RESTART is a pause rather than an error, which means restarts land in the middle
   * of forks — and the owner deploys often. The fork carries on server-side; the
   * orchestration record does not survive, so nothing here can ask about it either.
   *
   * So: no retry button. The endpoint is single-flight on `fork_id`, but that register
   * lives in memory too — after a restart a "retry" is not joining anything, it is
   * starting a SECOND fork. The one honest instruction is: go and look first.
   */
  return {
    title: 'Lost contact with the cloud while the fork was running',
    detail:
      (message ?? '').trim() ||
      'It may still be finishing. Check your agent list before trying again — a second attempt could start a second fork.',
    retry: false,
  }
}

/** Every code this build can put into words — the guard against the table drifting. */
export const FORK_FAILURE_CODES = Object.keys(FAILURES)

/**
 * The four legs, in the order they happen, each with the sentence to show while it is
 * the one running.
 *
 * Named legs with their own clocks instead of one spinner — avg-latency, 2026-08-22:
 * 太長的逾時會讓「死掉」看起來像「很慢」. A 300-second ceiling is right for the work
 * and useless to a reader: three minutes of a turning circle says nothing about whether
 * anything is happening. A leg that has been running 40 seconds says a great deal.
 *
 * `{machine}` is substituted by the caller, which knows the names; this module knows
 * the wording. Splitting it the other way would put a device lookup in a lookup table.
 */
const FORK_LEGS: { leg: ForkLeg; label: string }[] = [
  { leg: 'conversation', label: 'Opening a channel between the machines' },
  { leg: 'asking_source', label: 'Asking {source} for the conversation' },
  { leg: 'handing_key', label: 'Handing the key to {target}' },
  { leg: 'creating', label: 'Starting the agent on {target}' },
]

/**
 * What to say about a stage. `unknown` is the one that earns its own branch:
 *
 * it means this instance has no record of that fork — another instance ran it, or the
 * record aged out — and it is NOT a failure. Rendered as one, it would tell somebody
 * their fork failed when it may well have succeeded elsewhere, which sends them to
 * start a second one. So it says what it actually knows: nothing.
 */
export function forkStageLabel(
  stage: ForkStage | undefined,
  names: { source: string; target: string },
): string {
  switch (stage) {
    case 'done':
      return 'Done'
    case 'failed':
      return 'It stopped partway'
    case 'unknown':
      /*
       * NOT a failure, and after a deploy it is the EXPECTED answer: the orchestration
       * record lives in the BFF's memory, so a restart mid-fork erases it while the
       * fork itself carries on. avg-latency, 2026-08-22 — this went from a theoretical
       * distinction to something that happens on every deploy, and the owner deploys
       * often. Read as failure, it would tell someone their fork died at the exact
       * moment it was most likely still running.
       */
      return 'No progress to report here — the cloud may have restarted. That does not mean it failed'
    case undefined:
      return 'Starting…'
    default: {
      const row = FORK_LEGS.find((l) => l.leg === stage)
      return row ? fill(row.label, names) : String(stage)
    }
  }
}

function fill(label: string, names: { source: string; target: string }): string {
  return label.replace('{source}', names.source).replace('{target}', names.target)
}

/**
 * The legs as the UI draws them: every leg always present and in order, each marked
 * done / running / waiting, with the seconds the server reported for it.
 *
 * Built from the FIXED list rather than from the response's array, so a leg the server
 * has not mentioned yet still has a row. A progress display that grows a step at a time
 * cannot be read as progress — you cannot see how far along you are if you cannot see
 * how far there is to go.
 */
export type ForkLegView = {
  leg: ForkLeg
  label: string
  state: 'done' | 'running' | 'waiting'
  seconds: number | null
}

export function forkLegViews(
  progress: ForkProgress | undefined,
  names: { source: string; target: string },
): ForkLegView[] {
  const reported = new Map((progress?.legs ?? []).map((l) => [l.leg, l]))
  const stage = progress?.stage
  return FORK_LEGS.map(({ leg, label }) => {
    const row = reported.get(leg)
    const state: ForkLegView['state'] =
      row?.finished || stage === 'done' ? 'done' : stage === leg ? 'running' : row ? 'done' : 'waiting'
    return {
      leg,
      label: fill(label, names),
      state,
      seconds: typeof row?.elapsed_s === 'number' ? row.elapsed_s : null,
    }
  })
}

/* ── how often to ask where a fork has got to ─────────────────────────────── */

/** Dense while a normal fork is still running — it finishes in ~4.5s. */
const DENSE_MS = 250
const DENSE_FOR_MS = 5_000
/** Then back off, so a hung one does not cost a request every quarter second. */
const SLOW_START_MS = 2_000
const SLOW_CAP_MS = 5_000

/**
 * The gap before the next progress request.
 *
 * The cadence is avg-latency's, and both halves of it are load-bearing:
 *
 *   first 5s   every 250ms   a typical fork (4.5s end to end) finishes entirely
 *                            inside this window, so the legs update as fast as a
 *                            person can read them — it FEELS live, which is the
 *                            whole point of showing legs instead of a spinner.
 *   after 5s   2s → 4s → 5s  a fork that is going to take its full 300s does not
 *                            buy 720 requests to say "still asking the source".
 *
 * Counted from `slowPolls` rather than derived from the clock so the backoff is a
 * plain doubling that a reader can follow, and a test can drive without timers.
 *
 * Measured over a full 180s hang: 20 dense + 2 ramp + ~34 capped ≈ 56 requests,
 * against 720 at a flat 250ms. That number is the reason for the shape, so
 * forkAcross.test.ts asserts it rather than asserting the constants — constants
 * can be changed to match a wrong implementation; the total cannot.
 */
export function forkPollDelay(elapsedMs: number, slowPolls: number): number {
  if (elapsedMs < DENSE_FOR_MS) return DENSE_MS
  return Math.min(SLOW_CAP_MS, SLOW_START_MS * 2 ** Math.max(0, slowPolls))
}

/* ── which machines you may fork ONTO ─────────────────────────────────────── */

export type ForkTarget = {
  deviceId: string
  /**
   * nickname, else hostname, else the id — the display rule the whole app follows.
   *
   * `||`, NOT `??`. The BFF sends `nickname: ''` for a machine nobody has renamed —
   * not null — so `??` keeps the empty string and the row falls through to a raw
   * UUID. Found on the live dev account, where two of four machines rendered as
   * `e96d193b-29b2-…` instead of `tbc-m1` and `spark-244c`: a picker of UUIDs, from
   * one wrong operator. The unit tests had `nickname` absent or set, never empty.
   */
  name: string
  selectable: boolean
  /** Why it cannot be chosen. Empty when it can. */
  why: string
}

/**
 * The account's machines as fork destinations, with the source removed and the
 * unreachable ones SHOWN rather than hidden.
 *
 * Hiding a machine that cannot be used makes a person believe it is not there — they
 * go looking for why they never paired it. Showing it, disabled, with the reason,
 * answers the question they were about to ask (avg-devenv agreed on this one
 * explicitly: 隱藏會讓人以為那台不存在).
 *
 * `live` IS THREE-STATE, and getting that wrong blocks working machines:
 *
 *   true   this BFF instance holds the link → selectable
 *   false  no instance has ever seen it     → the one case we refuse locally
 *   null   this instance cannot tell — a SIBLING may hold the link → SELECTABLE
 *
 * `null` must go through. The gate on the server side is `links.is_live()`, which
 * looks only at the instance serving the request; a UI that treats "I don't know" as
 * "no" refuses a machine that is up. dev has no siblings today (every row reads
 * `true`), so this branch never fires there — which is exactly why it is written down
 * and tested rather than discovered on production.
 *
 * Nothing else is refused here, deliberately. `pending_unpair`, an old build, a
 * missing hostname — the server owns those judgements and answers with a named error
 * if it minds. Refusing locally on a rule the server does not have is how the UI ends
 * up blocking something that would have worked.
 */
/**
 * Can this agent be forked onto another machine at all, as the CLOUD reports it?
 *
 * `/account/devices` carries `fork_cross` per agent (bff/app.py:2934, a5a56c6). The
 * rule belongs to the edge — `forkPlan` supports claude and codex and refuses the rest
 * — and re-deriving it here from `cli` would drift the moment a CLI changes tier.
 *
 * TRI-STATE, AND THE THIRD STATE IS THE POINT. `null` means the machine never said,
 * because an edge that predates the field sends nothing. "Never told us" is not
 * "cannot be forked", and treating it as false hides a button that works — the same
 * distinction the unknown-code branch had to learn on the failure side. So this
 * returns `false` ONLY when the cloud said false.
 */
// Not exported: the two questions have their own names below, and a caller reaching
// for the pair is a caller about to collapse them again.
function forkFlagsOf(
  devices: AccountDevice[] | undefined,
  agentHash: string | null | undefined,
): { local: boolean | null; cross: boolean | null } {
  if (agentHash) {
    for (const d of devices ?? []) {
      for (const a of d.agents ?? []) {
        if (a.channel_hash === agentHash) {
          return { local: a.fork_local ?? null, cross: a.fork_cross ?? null }
        }
      }
    }
  }
  return { local: null, cross: null }
}

/**
 * Can this agent be forked AT ALL, on its own machine?
 *
 * Owner, 2026-08-22: 如果不能 Fork Antigravity / Cursor，那麼從 Frontend 這裡就不該
 * 顯示這個按鈕. Closing the machine picker was right and not enough — cursor and agy
 * are `fork_local=false` too, so the row itself offered something that could only fail.
 *
 * The two flags are NOT the same question, and codex is why: `fork_local=true`,
 * `fork_cross=false` — it forks on its own machine and not onto another. So this
 * decides whether the ROW exists, and `cross` decides what the picker inside it
 * offers. Collapsing them would take a working action away from every codex agent.
 */
export function forkLocalOf(
  devices: AccountDevice[] | undefined,
  agentHash: string | null | undefined,
): boolean | null {
  return forkFlagsOf(devices, agentHash).local
}

export function forkCrossOf(
  devices: AccountDevice[] | undefined,
  agentHash: string | null | undefined,
): boolean | null {
  return forkFlagsOf(devices, agentHash).cross
}

export function forkTargets(
  devices: AccountDevice[] | undefined,
  sourceDeviceId: string | null | undefined,
  /**
   * `false` closes every other machine, with the reason in the row a person reads.
   * Anything else leaves them open — see `forkCrossOf` for why absence must not gate.
   */
  forkCross?: boolean | null,
): ForkTarget[] {
  const rows: ForkTarget[] = []
  const refused = forkCross === false
  for (const d of devices ?? []) {
    if (!d.device_id || d.device_id === sourceDeviceId) continue
    const offline = d.live === false
    rows.push({
      deviceId: d.device_id,
      name: deviceDisplayName(d),
      selectable: !offline && !refused,
      why: refused
        ? // NO LIST OF KINDS. This said "Claude conversations only" when codex forked
          // only on its own machine (2026-08-22); codex and then Hermes (2026-10-15)
          // learned to cross, and the sentence went on naming one CLI on a control the
          // edge had opened for three. The edge's flag decides; the words only say what
          // it means for THIS agent.
          'This agent’s conversation can’t be carried to another machine'
        : offline
          ? 'Not connected right now'
          : '',
    })
  }
  // Usable ones first, then the ones that cannot be chosen — both visible. Within a
  // group, by name, so the list does not reshuffle as liveness flickers.
  return rows.sort(
    (a, b) => Number(b.selectable) - Number(a.selectable) || a.name.localeCompare(b.name),
  )
}
